// Edit V1 questionnaire details and behaviour while preserving legacy save contracts.
import { flowFieldErrors } from './flowFieldValidation';
import FlowFieldErrors from './FlowFieldErrors';
import FlowDraftPanel from './FlowDraftPanel';
import './flowAdmin.css';
import QuestionnaireStatusCard from './QuestionnaireStatusCard';
// Edit V1 questionnaire settings, rewards and independent WhatsApp discovery options.
import FlowLengthHint from './FlowLengthHint';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { toast } from 'react-toastify';
import {
  Form,
  Button,
  Icon,
  Checkbox,
  Radio,
  Divider,
  Dropdown,
  Table,
  Container,
  Dimmer,
  Loader,
  Confirm,
  Modal,
  Message,
  Segment,
  Tab,
  Header,
} from 'semantic-ui-react';
import * as QUESTIONAIRE_ACTIONS from '../../../actions/questionaireActions';
import * as QUESTIONAIRE_TYPE_ACTIONS from '../../../actions/questionaireTypeActions';
import * as QNAIREBR_ACTIONS from '../../../actions/brQnaireActions';
import * as COUNTRY_CODES_ACTION from '../../../actions/countryCodesActions';
import * as EXTERNAL_API_ACTIONS from '../../../actions/externalApiActions';
import { AVAILABLE_TRANSLATION_LANGUAGES as AVAILABLE_LANGUAGES } from '../../../constants/translations';
import ApiDocumentationTab from './ApiDocumentationTab';

// Disabled legacy settings may retain old values. Display those as empty without
// enabling anything; use the same defaults for dirty comparisons.
export const questionnaireFormValues = (questionnaire) => ({
  ...questionnaire,
  whatsappShareVisible: questionnaire.whatsappShareVisible ?? questionnaire.whatsappMenuVisible === true,
  activationPhrase: questionnaire.defaultQuestionnaire ? questionnaire.activationPhrase || '' : '',
  referralRewardValue: questionnaire.referralReward ? questionnaire.referralRewardValue || 0 : 0,
  languages: questionnaire.translateQnaire ? questionnaire.languages || [] : [],
});

const ROOT_QUESTIONNAIRE_TYPES = [
  { key: 1, text: 'My Profile', value: 'My Profile', Description: 'My Profile' },
  {
    key: 2,
    text: 'My Home and Properties',
    value: 'My Home and Properties',
    Description: 'My Home and Properties',
  },
  {
    key: 3,
    text: 'My Skills and Work Experience',
    value: 'My Skills and Work Experience',
    Description: 'My Skills and Work Experience',
  },
  {
    key: 4,
    text: 'My Will and Testament',
    value: 'My Will and Testament',
    Description: 'My Will and Testament',
  },
  { key: 5, text: 'POPIA', value: 'POPIA', Description: 'POPIA' },
  {
    key: 6,
    text: 'Community Marketplace',
    value: 'Community Marketplace',
    Description: 'Community Marketplace',
  },
  {
    key: 7,
    text: 'Business Registration',
    value: 'Business Registration',
    Description: 'Business Registration',
  },
  { key: 7, text: 'Incident Report', value: 'Incident Report', Description: 'Incident Report' },
];

const COUNTRY_CODES = [
  { key: 'MZN', value: 'mozambique', text: 'Mozambique', code: '+258' },
  { key: 'ZAR', value: 'south_africa_2', text: 'South Africa', code: '+27' },
];

// Number formatting utility functions
const formatNumberWithCommas = (value) => {
  if (!value) return '';
  const parts = value.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

const parseFormattedNumber = (value) => {
  return value.replace(/,/g, '');
};

// Referral Reward Input Component
const ReferralRewardInput = ({ state, updateQuestionnaireState, currencyList, handleWheel }) => {
  const [localReferralValue, setLocalReferralValue] = useState(
    state.questionaire.referralRewardValue
      ? formatNumberWithCommas((state.questionaire.referralRewardValue / 100).toString())
      : '',
  );

  useEffect(() => {
    setLocalReferralValue(
      state.questionaire.referralRewardValue
        ? formatNumberWithCommas((state.questionaire.referralRewardValue / 100).toString())
        : '',
    );
  }, [state.questionaire.referralRewardValue]);

  const handleReferralChange = (e) => {
    const inputValue = parseFormattedNumber(e.target.value);
    const formattedValue = formatNumberWithCommas(inputValue);
    setLocalReferralValue(formattedValue);

    if (inputValue === '' || inputValue === '0') {
      updateQuestionnaireState({ referralRewardValue: 0, referralReward: false });
    } else {
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue) && numericValue >= 0) {
        updateQuestionnaireState({
          referralRewardValue: Math.round(numericValue * 100),
          referralReward: Math.round(numericValue * 100) > 0,
        });
      }
    }
  };

  const currencyOptions = [
    { key: 'none', text: 'None', value: 'None' },
    ...currencyList.map((code) => ({
      key: code.key,
      text: code.key,
      value: code.key,
    })),
  ];

  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <Dropdown
        fluid
        placeholder="Currency"
        search
        selection
        style={{ minWidth: '120px', maxWidth: '150px' }}
        options={currencyOptions}
        value={state.questionaire.referralRewardCurrency || 'None'}
        onChange={(e, { value: selectedValue }) => {
          updateQuestionnaireState({ referralRewardCurrency: selectedValue });
        }}
      />
      <input
        type="text"
        aria-label="Referral reward amount"
        disabled={state.questionaire.defaultQuestionnaire && !state.questionaire.referralReward}
        placeholder="0.00"
        style={{
          flex: 1,
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '14px',
        }}
        value={localReferralValue}
        onChange={handleReferralChange}
        onWheel={handleWheel}
      />
    </div>
  );
};

// Reward Dropdown Component
const RewardDropdown = ({ state, updateQuestionnaireState, currencyList, control, handleWheel }) => {
  const [localRewardValue, setLocalRewardValue] = useState(
    state.questionaire.qnaireReward
      ? formatNumberWithCommas((state.questionaire.qnaireReward / 100).toString())
      : '',
  );

  useEffect(() => {
    setLocalRewardValue(
      state.questionaire.qnaireReward
        ? formatNumberWithCommas((state.questionaire.qnaireReward / 100).toString())
        : '',
    );
  }, [state.questionaire.qnaireReward]);

  const handleRewardChange = (e) => {
    const inputValue = parseFormattedNumber(e.target.value);
    const formattedValue = formatNumberWithCommas(inputValue);
    setLocalRewardValue(formattedValue);

    if (inputValue === '' || inputValue === '0') {
      updateQuestionnaireState({ qnaireReward: 0 });
    } else {
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue) && numericValue >= 0) {
        updateQuestionnaireState({ qnaireReward: Math.round(numericValue * 100) });
      }
    }
  };

  const currencyOptions = [
    { key: 'none', text: 'None', value: 'None' },
    ...currencyList.map((code) => ({
      key: code.key,
      text: code.key,
      value: code.key,
    })),
  ];

  return (
    <Controller
      name="currency"
      control={control}
      defaultValue={state.questionaire.qnaireRewardCurrency || 'None'}
      rules={{
        validate: (value) => {
          if (state.questionaire.qnaireReward > 0 && value === 'None') {
            return 'Please select a currency when reward amount is greater than 0.';
          }
          return true;
        },
      }}
      render={({ field: { onChange, value }, fieldState }) => (
        <div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <Dropdown
              className={fieldState.error ? 'invalid-input-field' : ''}
              fluid
              placeholder="Currency"
              search
              selection
              style={{ minWidth: '120px', maxWidth: '150px' }}
              options={currencyOptions}
              value={value || state.questionaire.qnaireRewardCurrency || 'None'}
              onChange={(e, { value: selectedValue }) => {
                onChange(selectedValue);
                updateQuestionnaireState({ qnaireRewardCurrency: selectedValue });
              }}
            />
            <input
              type="text"
              aria-label="Completion reward amount"
              placeholder="0.00"
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
              value={localRewardValue}
              onChange={handleRewardChange}
              onWheel={handleWheel}
            />
          </div>
          {fieldState.error && <p className="validation-error">{fieldState.error.message}</p>}
        </div>
      )}
    />
  );
};

// Translations Tab Component (moved outside to prevent re-creation)
const TranslationsTab = ({ questionaire, updateTranslation, flowsEnabled }) => {
  const selectedLanguages = questionaire.languages || [];

  return (
    <Segment>
      {selectedLanguages.map((langCode) => {
        const language = AVAILABLE_LANGUAGES.find((lang) => lang.value === langCode);
        const languageName = language ? language.text : langCode;

        return (
          <div key={langCode} style={{ marginBottom: '20px' }}>
            <h4>{languageName}</h4>
            <Form.Field>
              <label>Name Translation:</label>
              <input
                placeholder={`Enter name translation for ${languageName}`}
                value={questionaire.translations?.name?.[langCode] || ''}
                onChange={(e) => updateTranslation('name', langCode, e.target.value)}
              />
              <FlowLengthHint
                menuPreview
                enabled={flowsEnabled}
                value={questionaire.translations?.name?.[langCode]}
                limit={30}
              />
            </Form.Field>
            <Form.Field>
              <label>Description Translation:</label>
              <textarea
                rows={3}
                placeholder={`Enter description translation for ${languageName}`}
                value={questionaire.translations?.description?.[langCode] || ''}
                onChange={(e) => updateTranslation('description', langCode, e.target.value)}
              />
              <FlowLengthHint
                menuPreview
                enabled={flowsEnabled}
                value={questionaire.translations?.description?.[langCode]}
                limit={300}
              />
            </Form.Field>
            <Divider />
          </div>
        );
      })}
    </Segment>
  );
};

// Disclosure flags are local UI state, never new backend settings.
const SettingRow = ({ label, checked, disabled, onChange, children, hint }) => (
  <div className="questionnaire-setting-row">
    <Checkbox
      id={`setting-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      toggle
      label={label}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
    {children && <div className="questionnaire-setting-input">{children}</div>}
    {hint && <small className="flow-field-help">{hint}</small>}
  </div>
);

export function QuestionaireDetails(props) {
  const history = useHistory();
  const statusInFlight = useRef(false);
  const saveInFlight = useRef(false);
  const operationResolver = useRef(null);
  const refreshSequence = useRef(0);
  const [operation, setOperation] = useState(null);
  const [operationError, setOperationError] = useState('');
  const [flowPreparation, setFlowPreparation] = useState(null);
  // Navigating away must cancel a queued operation, never send it after unmount.
  useEffect(() => () => operationResolver.current?.(null), []);
  const [state, setState] = useState({
    questionaire: questionnaireFormValues(props.questionaire),
    savedQuestionnaire: props.questionaire,
    statusBusy: false,
    saving: false,
    statusError: '',
    refreshError: '',
    refreshBusy: false,
    showConfirmIsActive: false,
    showConfirmEditIsActive: false,
    activeTab: 0,
    flowDefault: null,
    phraseExpanded: !!props.questionaire.defaultQuestionnaire,
    referralExpanded: !!props.questionaire.referralReward,
    completionExpanded: props.questionaire.qnaireReward > 0,
  });

  const [currencyList, setCurrencyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rewardInputValue, setRewardInputValue] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    reset,
  } = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const refreshQuestionnaire = useCallback(async (snapshot) => {
    if (!snapshot?.id || !snapshot?.tenantId || !props.fetchQuestionnaireRecord) return;
    const sequence = ++refreshSequence.current;
    setState((previous) => ({ ...previous, refreshBusy: true, refreshError: '' }));
    try {
      const saved = await props.fetchQuestionnaireRecord(snapshot);
      if (sequence !== refreshSequence.current || saveInFlight.current || statusInFlight.current) return;
      setState((previous) => {
        // A later save owns the form. Never replace it with an earlier read.
        if (previous.savedQuestionnaire.id !== snapshot.id ||
            previous.savedQuestionnaire.updatedAt !== snapshot.updatedAt) return previous;
        const baseline = questionnaireFormValues(previous.savedQuestionnaire);
        const values = questionnaireFormValues(saved);
        // Keep edits made while the read was in flight; lifecycle fields remain server-owned.
        for (const field of Object.keys(previous.questionaire)) {
          if (!['status', 'isActive'].includes(field) && !_.isEqual(previous.questionaire[field], baseline[field]))
            values[field] = previous.questionaire[field];
        }
        return { ...previous, savedQuestionnaire: saved, questionaire: values, refreshError: '' };
      });
    } catch {
      if (sequence === refreshSequence.current)
        setState((previous) => ({ ...previous, refreshError: 'Could not load the latest questionnaire. Refresh before changing its status.' }));
    } finally {
      if (sequence === refreshSequence.current)
        setState((previous) => ({ ...previous, refreshBusy: false }));
    }
  }, [props.fetchQuestionnaireRecord]);

  useEffect(() => {
    if (props.token && !props.isNew) refreshQuestionnaire(props.questionaire);
    return () => { refreshSequence.current += 1; };
  }, [props.token, props.isNew, props.questionaire.id, props.questionaire.tenantId, refreshQuestionnaire]);

  // Effects
  useEffect(() => {
    if (!props.token) return;
    props.fetchQuestionaireTypes();
    props.fetchCountryCodes();
    if (props.questionaire.id && props.questionaire.tenantId) props.fetchQnaireBRs(props.questionaire);
    props.fetchAllQnaires();

    // Set default countryCode if not already set
    if (!state.questionaire.countryCode && props.tenant?.operatingCountry) {
      updateQuestionnaireState({ countryCode: props.tenant.operatingCountry });
    }
  }, [props.token, props.questionaire.id, props.questionaire.tenantId]);

  useEffect(() => {
    if (props.token && props.tenant?.tenantId) props.fetchTenantCurrencies(props.tenant);
  }, [props.token, props.tenant?.tenantId]);

  // let codeslist = [];

  // let codeNone = {
  //   key: 1,
  //   value: '',
  //   text: ' None',
  // };
  // codeslist.push(codeNone);

  // Made the Country Codes hard coded!
  // codeslist.push({ key: "MZN", value: "mozambique", text: "Mozambique", code: "+258" },);
  // codeslist.push({ key: "ZAR", value: "south_africa_2", text: "South Africa", code: "+27" },);

  // codeslist = codeslist.map((item) => {
  //   // Safely check if props.tenant.operatingCountry.key exists
  //   if (
  //     props?.tenant?.operatingCountry?.key &&
  //     item.key === props.tenant?.operatingCountry.key
  //   ) {
  //     return props.tenant?.operatingCountry;
  //   }
  //   return item;
  // });

  // toast(codeslist);

  // props.tenant.operatingCountry

  // props.tenantCurrencies?.forEach((code, index) => {
  //   code = {
  //     key: code,
  //     value: code,
  //     text: code,
  //   };
  //   currencyList.push(code);
  // });

  useEffect(() => {
    if (props.tenantCurrencies) {
      const updatedCurrencyList = Object.entries(props.tenantCurrencies).map(([key, value]) => ({
        key,
        value,
        text: key,
      }));
      setCurrencyList(updatedCurrencyList);
      setLoading(false);
    }
  }, [props.tenantCurrencies]);

  useEffect(() => {
    setRewardInputValue(
      state.questionaire.qnaireReward ? (state.questionaire.qnaireReward / 100).toString() : '',
    );
  }, [state.questionaire.qnaireReward]);

  // Utility functions
  // Read the historical default from the service without silently persisting it.
  const receiveFlowDefault = useCallback((value) => {
    setState((previous) => (previous.flowDefault === value ? previous : { ...previous, flowDefault: value }));
  }, []);

  const updateQuestionnaireState = (updates) => {
    setState((prevState) => ({
      ...prevState,
      questionaire: { ...prevState.questionaire, ...updates },
    }));
  };

  const updateTranslation = (field, language, value) => {
    const translations = state.questionaire.translations || {};
    const fieldTranslations = translations[field] || {};

    updateQuestionnaireState({
      translations: {
        ...translations,
        [field]: {
          ...fieldTranslations,
          [language]: value,
        },
      },
    });
  };

  const handleWheel = (e) => {
    e.target.blur();
  };

  const validateActivationPhrase = () => {
    if (state.questionaire.isActive && state.questionaire.defaultQuestionnaire) {
      const activeDefaultQuestionnaires =
        props.allQuestionaires?.filter(
          (questionaire) =>
            (questionaire.isActive || questionaire.status === 'underReview') &&
            questionaire.defaultQuestionnaire &&
            questionaire.id !== state.questionaire.id,
        ) || [];

      const duplicateActivationPhrase = activeDefaultQuestionnaires.some(
        (questionaire) => questionaire.activationPhrase === state.questionaire.activationPhrase,
      );

      if (duplicateActivationPhrase) {
        toast.error(
          `Activation Phrase: '${state.questionaire.activationPhrase}' already in use. Please use a different phrase.`,
        );
        return false;
      }
    }
    return true;
  };

  // Event handlers
  const flowsEnabled = state.questionaire.whatsappFlowsEnabled ?? state.flowDefault ?? false;
  const flowErrors = flowFieldErrors(
    state.questionaire,
    { ...state.questionaire, whatsappFlowsEnabled: flowsEnabled },
    'questionnaire',
  );

  const validateQuestionnaireSave = () => {
    if (
      (state.referralExpanded && !(state.questionaire.referralRewardValue > 0)) ||
      (state.completionExpanded && !(state.questionaire.qnaireReward > 0))
    ) {
      toast.error('Enter a reward greater than zero, or switch the reward off.');
      return false;
    }
    if (state.phraseExpanded && !state.questionaire.activationPhrase?.replace(/^Hello /i, '').trim()) {
      toast.error('Enter an activation phrase, or switch Default WhatsApp questionnaire off.');
      return false;
    }
    if (
      state.questionaire.referralRewardValue > 0 &&
      (!state.questionaire.referralRewardCurrency || state.questionaire.referralRewardCurrency === 'None')
    ) {
      toast.error('Select a currency for the referral reward.');
      return false;
    }
    return validateActivationPhrase();
  };

  const onSubmit = async () => {
    if (!validateQuestionnaireSave()) return;
    const isQuestionnaireActive =
      state.savedQuestionnaire.status === 'active' || state.savedQuestionnaire.status === 'underReview';

    if (isQuestionnaireActive) {
      setState((state) => ({ ...state, showConfirmEditIsActive: true }));
      return;
    }

    await saveQuestionnaire(state.questionaire);
  };

  // A successful save updates the baseline in place, including generated IDs.
  // Failed saves keep the current draft and tab available for correction/retry.
  const saveQuestionnaire = async (source) => {
    if (saveInFlight.current || statusInFlight.current) return;
    saveInFlight.current = true;
    setFlowPreparation(null);
    setState((previous) => ({ ...previous, saving: true }));
    try {
      const saved = await props.commitQuestionaire(
        {
          ...source,
          countryCode: source.countryCode || props.tenant.operatingCountry,
        },
        !state.savedQuestionnaire.id,
      );
      if (!saved) return;
      setState((previous) => ({
        ...previous,
        savedQuestionnaire: saved,
        questionaire: questionnaireFormValues(saved),
        showConfirmEditIsActive: false,
      }));
      // Keep route state current; creation becomes editing rather than creating
      // another record on the next Save. Replacing preserves browser history.
      if (history.location.pathname.startsWith('/Questionaires/')) {
        history.replace(`/Questionaires/edit/${encodeURIComponent(saved.name)}`, {
          ...history.location.state,
          questionaire: saved,
        });
      }
      return saved;
    } finally {
      saveInFlight.current = false;
      setState((previous) => ({ ...previous, saving: false }));
    }
  };

  const handleToggleChange = (field) => {
    const newValue = !state.questionaire[field];
    const updates = { [field]: newValue };

    updateQuestionnaireState(updates);
  };

  const statusDirty = !_.isEqual(state.questionaire, questionnaireFormValues(state.savedQuestionnaire));
  // Operations share the parent's validation/save path. Resolve with the actual
  // saved record so children never continue using stale React props after saving.
  const beforeWhatsAppOperation = (label) => {
    if (operationResolver.current || saveInFlight.current || statusInFlight.current)
      return Promise.resolve(null);
    if (!statusDirty) return Promise.resolve(state.savedQuestionnaire);
    setOperationError('');
    setOperation(label);
    return new Promise((resolve) => {
      operationResolver.current = resolve;
    });
  };
  const finishWhatsAppConfirmation = (record) => {
    const resolve = operationResolver.current;
    operationResolver.current = null;
    setOperation(null);
    resolve?.(record);
  };
  const discardForWhatsAppOperation = () => {
    const saved = state.savedQuestionnaire;
    const values = questionnaireFormValues(saved);
    reset(values);
    setState((previous) => ({
      ...previous,
      questionaire: values,
      phraseExpanded: !!saved.defaultQuestionnaire,
      referralExpanded: !!saved.referralReward,
      completionExpanded: saved.qnaireReward > 0,
    }));
    finishWhatsAppConfirmation(saved);
  };
  const saveForWhatsAppOperation = async () => {
    if (!validateQuestionnaireSave()) {
      setOperationError('Correct the questionnaire settings before continuing.');
      return;
    }
    try {
      const saved = await saveQuestionnaire(state.questionaire);
      if (!saved) {
        setOperationError('The questionnaire was not saved. No operation was started.');
        return;
      }
      finishWhatsAppConfirmation(saved);
    } catch {
      setOperationError('The questionnaire was not saved. No operation was started.');
    }
  };

  const submittingApproval =
    !state.savedQuestionnaire.isActive && state.savedQuestionnaire.status !== 'underReview';

  const applyStatus = async (saveChanges = false) => {
    if (statusInFlight.current) return;
    const source = saveChanges ? state.questionaire : state.savedQuestionnaire;
    if (submittingApproval) {
      // The review service prepares and validates the saved Flow; display issues
      // must not prevent saving edits before requesting review.
      const duplicate = (props.allQuestionaires || []).some(
        (q) =>
          q.id !== source.id &&
          (q.isActive || q.status === 'underReview') &&
          q.defaultQuestionnaire &&
          source.defaultQuestionnaire &&
          q.activationPhrase === source.activationPhrase,
      );
      const duplicateCategory =
        ROOT_QUESTIONNAIRE_TYPES.some((type) => type.value === source.questionaireType) &&
        (props.questionaires || []).some(
          (q) =>
            q.id !== source.id &&
            q.isActive &&
            q.questionaireType === source.questionaireType &&
            q.countryCode?.value === source.countryCode?.value,
        );
      if (duplicate || duplicateCategory) {
        setState((previous) => ({
          ...previous,
          statusError: duplicate
            ? 'This activation phrase is already in use.'
            : 'An active questionnaire already uses this category and country.',
        }));
        return;
      }
      if (
        (source.referralReward &&
          (!(source.referralRewardValue > 0) ||
            !source.referralRewardCurrency ||
            source.referralRewardCurrency === 'None')) ||
        (source.qnaireReward > 0 &&
          (!source.qnaireRewardCurrency || source.qnaireRewardCurrency === 'None')) ||
        (source.defaultQuestionnaire && !source.activationPhrase?.replace(/^Hello /i, '').trim()) ||
        (saveChanges &&
          ((state.phraseExpanded && !source.activationPhrase?.replace(/^Hello /i, '').trim()) ||
            (state.referralExpanded && !(source.referralRewardValue > 0)) ||
            (state.completionExpanded && !(source.qnaireReward > 0))))
      ) {
        setState((previous) => ({
          ...previous,
          statusError: 'Complete the enabled activation phrase and reward settings before submitting.',
        }));
        return;
      }
    }
    statusInFlight.current = true;
    setState((previous) => ({ ...previous, statusBusy: true, statusError: '' }));
    try {
      const saved = await props.changeQuestionnaireStatus(source, submittingApproval, saveChanges, setFlowPreparation);
      setState((previous) => ({
        ...previous,
        savedQuestionnaire: saved,
        statusBusy: false,
        showConfirmIsActive: false,
        questionaire: submittingApproval
          ? questionnaireFormValues(saved)
          : { ...previous.questionaire, status: saved.status, isActive: saved.isActive },
        ...(submittingApproval
          ? {
              phraseExpanded: !!saved.defaultQuestionnaire,
              referralExpanded: !!saved.referralReward,
              completionExpanded: saved.qnaireReward > 0,
            }
          : {}),
      }));
      toast.success(submittingApproval ? 'Submitted for approval.' : 'Questionnaire is inactive.');
    } catch (error) {
      if (error.isFlowPreparationError) {
        setState((previous) => ({
          ...previous,
          statusBusy: false,
          statusError: '',
          showConfirmIsActive: false,
          ...(error.savedQuestionnaire ? {
            savedQuestionnaire: error.savedQuestionnaire,
            questionaire: questionnaireFormValues(error.savedQuestionnaire),
          } : {}),
        }));
        document.getElementById('whatsapp-flows-panel')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return;
      }
      setState((previous) => ({
        ...previous,
        statusBusy: false,
        statusError:
          error.userMessage ||
          error.response?.data?.error ||
          'Could not confirm the status change. Check the saved status before retrying.',
      }));
    } finally {
      statusInFlight.current = false;
    }
  };

  const handleIsActiveChange = () => {
    setState((previous) => ({ ...previous, showConfirmIsActive: true, statusError: '' }));
  };

  // Dropdown components
  const QuestionnaireTypesDropdown = () => {
    let questionaireTypes = [];

    if (!_.isEmpty(props.questionaireTypes)) {
      if (props.tenant.isRoot) {
        questionaireTypes = [
          ...ROOT_QUESTIONNAIRE_TYPES,
          ...props.questionaireTypes.map((item, index) => ({
            key: index + ROOT_QUESTIONNAIRE_TYPES.length,
            value: item.Name,
            text: item.Name,
          })),
        ];
        // ].sort((a, b) => a.text.localeCompare(b.text));
      } else {
        questionaireTypes = props.questionaireTypes
          .map((item, index) => ({
            key: index,
            value: item.Name,
            text: item.Name,
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
      }
    }

    const defaultValue = state.questionaire.questionaireType || '';

    return (
      <Controller
        name="questionaire_type"
        control={control}
        defaultValue={defaultValue}
        render={({ field: { onChange, value, ref }, fieldState }) => (
          <div>
            <div ref={ref} tabIndex={-1} className="dropdown-wrapper">
              <Dropdown
                className={fieldState.error ? 'invalid-input-field' : ''}
                fluid
                placeholder="Select questionnaire type"
                search
                selection
                loading={props.fetchingData}
                options={questionaireTypes}
                value={value || defaultValue}
                onChange={(e, { value: selectedValue }) => {
                  onChange(selectedValue);
                  updateQuestionnaireState({
                    isActive: false,
                    questionaireType: selectedValue,
                  });
                }}
              />
            </div>
            {fieldState.error && <p className="validation-error">* Select a type</p>}
          </div>
        )}
      />
    );
  };

  const LanguagesDropdown = () => (
    <Controller
      name="questionaire_languages"
      control={control}
      rules={{ required: false }}
      render={({ field: { onChange, value }, fieldState }) => (
        <div>
          <Dropdown
            className={fieldState.invalid ? 'invalid-input-field' : ''}
            placeholder="Select additional languages (optional)"
            fluid
            multiple
            selection
            search
            options={AVAILABLE_LANGUAGES}
            value={(state.questionaire.languages || []).filter((lang) => lang !== 'en')}
            onChange={(e, { value: selectedValue }) => {
              const languagesWithEnglish = selectedValue.length > 0 ? ['en', ...selectedValue] : [];
              onChange(languagesWithEnglish);
              updateQuestionnaireState({
                isActive: false,
                languages: languagesWithEnglish,
                translateQnaire: selectedValue.length > 0,
              });
            }}
          />
          <small className="flow-field-help">
            English is included. Leave empty to use English only without translation.
          </small>
        </div>
      )}
    />
  );

  const CountryCodeDropdown = () => {
    const codeslist = COUNTRY_CODES.map((item) => {
      if (props?.tenant?.operatingCountry?.key && item.key === props.tenant.operatingCountry.key) {
        return props.tenant.operatingCountry;
      }
      return item;
    });

    const defaultValue = state.questionaire.countryCode?.value || props.tenant?.operatingCountry?.value || '';

    return (
      <Controller
        name="questionnaire_countrycode"
        control={control}
        rules={{ required: false }}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => (
          <div>
            <Dropdown
              className={fieldState.invalid ? 'invalid-input-field' : ''}
              {...field}
              placeholder="Select country code"
              fluid
              selection
              search
              options={codeslist}
              value={defaultValue}
              onChange={(e, { value }) => {
                const selectedObject = codeslist.find((option) => option.value === value);
                updateQuestionnaireState({ countryCode: selectedObject });
                field.onChange(value);
              }}
            />
          </div>
        )}
      />
    );
  };

  // Table components
  const BusinessRulesTable = () => {
    if (props.fetchingData) {
      return (
        <div style={{ height: '200px', margin: '10px 0px' }}>
          <Dimmer inverted active>
            <Loader content="Loading" />
          </Dimmer>
        </div>
      );
    }

    if (_.isEmpty(props.qnaireBRs)) {
      return null;
    }

    return (
      <Table compact selectable size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="nectaPrimaryBg">
              Business Rules linked to Questionnaire
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {props.qnaireBRs.map((qnaireBR) => (
            <Table.Row key={qnaireBR.id}>
              <Table.Cell>{qnaireBR.brName}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  // Confirmation dialogs
  const ConfirmActivate = () => (
    <Modal
      open={state.showConfirmIsActive}
      size="small"
      closeOnDimmerClick={false}
      closeOnEscape={!state.statusBusy}
      onClose={() =>
        !state.statusBusy && setState((previous) => ({ ...previous, showConfirmIsActive: false }))
      }
    >
      <Modal.Header>
        {submittingApproval
          ? 'Submit for approval?'
          : state.savedQuestionnaire.status === 'underReview'
            ? 'Withdraw from review?'
            : 'Deactivate questionnaire?'}
      </Modal.Header>
      <Modal.Content>
        <p>
          {submittingApproval
            ? statusDirty
              ? 'You have unsaved changes. Save them before submitting, or discard them and submit the saved questionnaire.'
              : 'Submit the saved questionnaire for review. It becomes available to respondents only after approval.'
            : 'This changes the saved status immediately. New respondents cannot start an inactive questionnaire. Any unsaved form edits will remain here.'}
        </p>
        {state.statusError && <Message visible negative content={state.statusError} />}
      </Modal.Content>
      <Modal.Actions>
        <Button
          type="button"
          disabled={state.statusBusy}
          onClick={() => setState((previous) => ({ ...previous, showConfirmIsActive: false }))}
        >
          Cancel
        </Button>
        {submittingApproval && statusDirty ? (
          <>
            <Button type="button" disabled={state.statusBusy} onClick={() => applyStatus(false)}>
              Discard changes &amp; submit
            </Button>
            <Button
              type="button"
              primary
              disabled={state.statusBusy}
              loading={state.statusBusy || state.saving}
              onClick={handleSubmit(
                () => applyStatus(true),
                () =>
                  setState((previous) => ({
                    ...previous,
                    statusError: 'Correct the highlighted form fields before submitting.',
                  })),
              )}
            >
              Save changes &amp; submit
            </Button>
          </>
        ) : (
          <Button
            type="button"
            primary
            disabled={state.statusBusy}
            loading={state.statusBusy || state.saving}
            onClick={() => applyStatus(false)}
          >
            Confirm
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );

  const ConfirmEditActive = () => {
    const contentMessage =
      'The questionnaire is currently active or under review. Editing any part of the questionnaire will result in its deactivation, requiring it to undergo the review process again.';

    return (
      <Confirm
        header="Warning:"
        content={contentMessage}
        open={state.showConfirmEditIsActive}
        onCancel={() => setState((state) => ({ ...state, showConfirmEditIsActive: false }))}
        onConfirm={() => {
          if (!validateActivationPhrase()) {
            return;
          }

          const updatedQuestionaire = { ...state.questionaire, status: 'inActive', isActive: false };
          saveQuestionnaire(updatedQuestionaire);
        }}
      />
    );
  };

  // Render form content function
  const renderFormContent = () => (
    <>
      <QuestionnaireStatusCard
        saved={state.savedQuestionnaire}
        current={state.questionaire}
        isNew={props.isNew}
        busy={state.statusBusy || state.refreshBusy || !!state.refreshError}
        onChange={handleIsActiveChange}
      />
      {state.refreshError && <Message visible negative role="alert" content={state.refreshError} />}
      <Segment className="questionnaire-card questionnaire-details-card">
        <h3>Details</h3>
        {/* Basic Information */}
        <Form.Field>
          <label>Name:</label>
          <input
            name="questionaire_name"
            className={errors.questionaire_name ? 'invalid-input-field' : ''}
            {...register('questionaire_name', {
              required: true,
              pattern: /^[A-Za-z0-9-\s]*$/,
            })}
            placeholder="Enter questionnaire name"
            value={state.questionaire.name}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (inputValue.length <= 50) {
                updateQuestionnaireState({ name: inputValue });
              }
            }}
          />
          <FlowLengthHint menuPreview enabled={flowsEnabled} value={state.questionaire.name} limit={30} />
          {errors.questionaire_name?.type === 'required' && (
            <p className="validation-error">* Please enter a name</p>
          )}
          {errors.questionaire_name?.type === 'pattern' && (
            <p className="validation-error">* May only contain alphanumerical characters and "-"</p>
          )}
        </Form.Field>

        {!props.isNew && (
          <Form.Field>
            <label>Merge field:</label>
            <input className="visible-input" value={state.questionaire.pptRef} disabled />
          </Form.Field>
        )}

        <Form.Field>
          <label>Description:</label>
          <textarea
            name="questionaire_description"
            className={errors.questionaire_description ? 'invalid-input-field' : ''}
            {...register('questionaire_description', {
              required: true,
              maxLength: 512,
            })}
            placeholder="Enter a description for the questionnaire"
            value={state.questionaire.description}
            onChange={(e) => updateQuestionnaireState({ description: e.target.value })}
          />
          <FlowLengthHint menuPreview enabled={flowsEnabled} value={state.questionaire.description} limit={300} />
          {errors.questionaire_description?.type === 'required' && (
            <p className="validation-error">* Please enter a description</p>
          )}
          {errors.questionaire_description?.type === 'maxLength' && (
            <p className="validation-error">* Description may not be more than 512 characters long</p>
          )}
        </Form.Field>

        <Form.Field>
          <label>Category:</label>
          <QuestionnaireTypesDropdown />
        </Form.Field>

        {props.tenant.isRoot && (
          <Form.Field>
            <label>Country code:</label>
            <CountryCodeDropdown />
          </Form.Field>
        )}

        <Form.Field>
          <label>Languages:</label>
          <LanguagesDropdown />
        </Form.Field>
      </Segment>
      <Segment className="questionnaire-card questionnaire-settings-card">
        <h3>Settings</h3>
        <SettingRow
          label="Default WhatsApp questionnaire"
          checked={state.phraseExpanded}
          disabled={state.referralExpanded && !state.phraseExpanded}
          onChange={() => {
            const enabled = !state.phraseExpanded;
            setState((previous) => ({ ...previous, phraseExpanded: enabled }));
            if (!enabled) updateQuestionnaireState({ defaultQuestionnaire: false, activationPhrase: '' });
          }}
        >
          {state.phraseExpanded && (
            <Form.Field>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: '5px' }}>Hello</div>
                <input
                  name="activationPhrase"
                  aria-label="Activation phrase"
                  className={errors.activationPhrase ? 'invalid-input-field' : ''}
                  {...register('activationPhrase', {
                    required: false,
                    maxLength: 45,
                  })}
                  placeholder="Enter the rest of the activation phrase"
                  disabled={state.questionaire.referralReward && !state.questionaire.defaultQuestionnaire}
                  value={state.questionaire.activationPhrase?.replace(/^Hello /i, '') || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value.replace(/_/g, ''); // Remove underscores
                    const cleanedValue = inputValue.toLowerCase() === 'hello' ? '' : inputValue;
                    if (cleanedValue.length <= 50) {
                      updateQuestionnaireState({
                        activationPhrase: cleanedValue.trim() ? `Hello ${cleanedValue}` : '',
                        defaultQuestionnaire: Boolean(cleanedValue.trim()),
                      });
                    }
                  }}
                />
              </div>
              {errors.activationPhrase?.type === 'required' && (
                <p className="validation-error">* Please enter an activation phrase</p>
              )}
              {errors.activationPhrase?.type === 'maxLength' && (
                <p className="validation-error">
                  * Activation phrase may not be more than 50 characters long
                </p>
              )}
            </Form.Field>
          )}
        </SettingRow>
        <SettingRow
          label="Referral reward"
          checked={state.referralExpanded}
          disabled={state.phraseExpanded && !state.referralExpanded}
          onChange={() => {
            const enabled = !state.referralExpanded;
            setState((previous) => ({ ...previous, referralExpanded: enabled }));
            if (!enabled) updateQuestionnaireState({ referralReward: false, referralRewardValue: 0 });
          }}
        >
          {state.referralExpanded && (
            <ReferralRewardInput
              state={state}
              updateQuestionnaireState={updateQuestionnaireState}
              currencyList={currencyList}
              handleWheel={handleWheel}
            />
          )}
        </SettingRow>
        {props.tenant.isRoot && (
          <SettingRow
            label="Base questionnaire"
            checked={!!state.questionaire.isBaseQnaire}
            onChange={() => handleToggleChange('isBaseQnaire')}
          />
        )}
        <SettingRow
          label="Completion reward"
          checked={state.completionExpanded}
          onChange={() => {
            const enabled = !state.completionExpanded;
            setState((previous) => ({ ...previous, completionExpanded: enabled }));
            if (!enabled) updateQuestionnaireState({ qnaireReward: 0 });
          }}
        >
          {state.completionExpanded && (
            <RewardDropdown
              state={state}
              updateQuestionnaireState={updateQuestionnaireState}
              currencyList={currencyList}
              control={control}
              handleWheel={handleWheel}
            />
          )}
        </SettingRow>
        <SettingRow
          label="Allow multiple responses"
          checked={!!state.questionaire.multiple}
          onChange={() => handleToggleChange('multiple')}
        />
        <SettingRow
          label="Allow response editing"
          checked={!!state.questionaire.canEditQnaire}
          onChange={() => handleToggleChange('canEditQnaire')}
        />
        <SettingRow
          label="Anonymise responses"
          checked={!!state.questionaire.anonymisedData}
          disabled={state.questionaire.alwaysAnonymise}
          onChange={() => handleToggleChange('anonymisedData')}
        />
        <SettingRow
          label="POPIA permission required"
          checked={!!state.questionaire.isPersonalInfo}
          onChange={() => handleToggleChange('isPersonalInfo')}
        />
        <Container>
          <BusinessRulesTable />
        </Container>
      </Segment>
      <FlowDraftPanel
        questionnaire={state.savedQuestionnaire}
        onQuestionnaireRefresh={refreshQuestionnaire}
        preparationFeedback={flowPreparation}
        onPreparationFeedback={setFlowPreparation}
        onDeliveryMode={receiveFlowDefault}
        dirty={statusDirty}
        beforeOperation={beforeWhatsAppOperation}
      >
        <h3>WhatsApp</h3>
        <section aria-labelledby="whatsapp-delivery-heading">
          <h4 id="whatsapp-delivery-heading">Delivery</h4>
          <p>Choose how respondents complete this questionnaire on WhatsApp.</p>
          <div role="radiogroup" aria-label="WhatsApp delivery mode">
            {[
              ['Messages', false, 'Respondents answer questions through a WhatsApp conversation.'],
              ['WhatsApp Flows', true, 'Respondents answer questions using interactive WhatsApp screens.'],
            ].map(([label, value, description]) => (
              <Form.Field key={label}>
                <Radio
                  id={`whatsapp-delivery-${value}`}
                  name="whatsapp-delivery-mode"
                  label={label}
                  checked={(state.questionaire.whatsappFlowsEnabled ?? state.flowDefault ?? false) === value}
                  disabled={
                    !props.isNew &&
                    state.questionaire.whatsappFlowsEnabled == null &&
                    state.flowDefault == null
                  }
                  onChange={() => updateQuestionnaireState({ whatsappFlowsEnabled: value })}
                />
                <small className="flow-field-help">{description}</small>
              </Form.Field>
            ))}
          </div>
        </section>
        <Divider />
        <section aria-labelledby="whatsapp-hello-heading">
          <h4 id="whatsapp-hello-heading">Hello Wakamoso</h4>
          <p>Choose where this questionnaire appears in the Hello Wakamoso menu.</p>
          <Form.Field>
            <Checkbox
              id="whatsapp-menu-visible"
              label="Show under Questionnaires"
              checked={!!state.questionaire.whatsappMenuVisible}
              disabled={!props.isTenantZero}
              onChange={() => handleToggleChange('whatsappMenuVisible')}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              id="whatsapp-share-visible"
              label="Show under Share"
              checked={!!state.questionaire.whatsappShareVisible}
              disabled={!props.isTenantZero}
              onChange={() => handleToggleChange('whatsappShareVisible')}
            />
            {(!state.questionaire.referralReward || !state.questionaire.pptRef) && (
              <small className="flow-field-help">
                Configure a referral reward and sharing code to appear under Share.
              </small>
            )}
          </Form.Field>
        </section>
        <Divider />
      </FlowDraftPanel>
    </>
  );

  return (
    <Segment style={{ backgroundColor: 'white', padding: '20px' }}>
      <Form size="small" loading={state.statusBusy || state.saving} onSubmit={handleSubmit(onSubmit)}>
        <FlowFieldErrors errors={flowErrors} />
        <ConfirmActivate />
        <ConfirmEditActive />
        <Modal
          open={Boolean(operation)}
          size="small"
          closeOnDimmerClick={false}
          closeOnEscape={!state.saving}
          onClose={() => !state.saving && finishWhatsAppConfirmation(null)}
        >
          <Modal.Header>{operation}?</Modal.Header>
          <Modal.Content>
            <p>
              You have unsaved questionnaire changes. Save them before continuing, or discard them and use the
              saved questionnaire.
            </p>
            {(state.savedQuestionnaire.isActive ||
              ['active', 'underReview'].includes(state.savedQuestionnaire.status)) && (
              <Message
                visible
                warning
                content="Saving changes will deactivate this questionnaire and require approval again. Discarding changes keeps its current status."
              />
            )}
            {operationError && <Message visible negative content={operationError} />}
          </Modal.Content>
          <Modal.Actions>
            <Button type="button" disabled={state.saving} onClick={() => finishWhatsAppConfirmation(null)}>
              Cancel
            </Button>
            <Button type="button" disabled={state.saving} onClick={discardForWhatsAppOperation}>
              Discard changes &amp; continue
            </Button>
            <Button
              type="button"
              primary
              disabled={state.saving}
              loading={state.saving}
              onClick={handleSubmit(saveForWhatsAppOperation, () =>
                setOperationError('Correct the highlighted form fields before continuing.'),
              )}
            >
              Save changes &amp; continue
            </Button>
          </Modal.Actions>
        </Modal>

        {state.questionaire.translateQnaire && !props.isNew ? (
          <Tab
            activeIndex={state.activeTab}
            onTabChange={(e, { activeIndex }) => setState((prev) => ({ ...prev, activeTab: activeIndex }))}
            panes={[
              {
                menuItem: 'General',
                render: () => <Tab.Pane>{renderFormContent()}</Tab.Pane>,
              },
              {
                menuItem: 'Translations',
                render: () => (
                  <Tab.Pane>
                    <TranslationsTab
                      questionaire={state.questionaire}
                      updateTranslation={updateTranslation}
                      flowsEnabled={flowsEnabled}
                    />
                  </Tab.Pane>
                ),
              },
            ]}
          />
        ) : (
          renderFormContent()
        )}

        <Divider />
        <Button.Group floated="right">
          <Button type="button" className="danger" onClick={() => history.push('/Questionnaires')}>
            <Icon name="remove" /> Cancel
          </Button>
          <Button type="submit" className="success" disabled={state.saving || state.refreshBusy || !!state.refreshError}>
            <Icon name="checkmark" /> Save changes
          </Button>
        </Button.Group>
        <br />
      </Form>
    </Segment>
  );
}

const mapStateToProps = (state, ownProps) => {
  let questionaire = {};
  let isNew = false;

  const newQuestionaire = {
    name: '',
    description: '',
    countryCode: '',
    multiple: false,
    defaultQuestionnaire: false,
    whatsappMenuVisible: false,
    whatsappShareVisible: false,
    pptRef: '',
    isActive: false,
    isBaseQnaire: false,
    isPersonalInfo: false,
    canEditQnaire: false,
    hasPptTemplates: false,
    translateQnaire: false,
    referralReward: false,
    questionaireType: '',
    languages: [],
    status: 'inActive',
    activationPhrase: '',
    referralRewardValue: 0,
    referralRewardCurrency: 'None',
    tenantId: state.tenantManagement.activeTenant.tenantId,
  };

  // Check if questionnaire data comes from route state (for navigation) or props (for modal)
  const routeQuestionaire = ownProps.location?.state?.questionaire;
  const routeQuestionaires = ownProps.location?.state?.questionaires;

  if (routeQuestionaire) {
    // Use questionnaire from route state
    questionaire = routeQuestionaire;
    isNew = false;
  } else if (ownProps.questionaire === undefined) {
    questionaire = newQuestionaire;
    isNew = true;
  } else {
    questionaire = _.isEmpty(ownProps.questionaires)
      ? newQuestionaire
      : ownProps.questionaires.find((q) => q.id === ownProps.questionaire.id) || newQuestionaire;
  }

  const questionaireTypes = _.isEmpty(state.questionaireTypes) ? [] : Object.values(state.questionaireTypes);
  const chatbotQuestionaireType =
    questionaireTypes.find((questionaireType) => questionaireType.Name.toLowerCase() === 'chatbot') || {};

  const qnaireBRs = _.isEmpty(state.qnaireBRs)
    ? []
    : state.qnaireBRs.filter((qnaireBR) => qnaireBR.qnaireId === questionaire.id);

  return {
    token: state.authData?.[0]?.signInUserSession?.accessToken?.jwtToken,
    isTenantZero: (
      state.authData?.[0]?.signInUserSession?.accessToken?.payload?.['cognito:groups'] || []
    ).includes('TenantZero'),
    fetchingData: state.fetchingData === true,
    questionaire,
    questionaireTypes: state.questionaireTypes,
    chatbotQuestionaireType,
    questionaires: state.questionaires,
    allQuestionaires: state.allQuestionnaires,
    isNew,
    qnaireBRs,
    countryCodes: _.isEmpty(state.countryCodes) ? [] : _.sortBy(state.countryCodes, 'countryCode'),
    tenant: state.tenantManagement.activeTenant,
    tenantCurrencies: state.tenantFinances?.tenantBalances?.currencies,
    externalApi: state.externalApi,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchQuestionnaireRecord: (questionnaire) => dispatch(QUESTIONAIRE_ACTIONS.fetchQuestionnaireRecord(questionnaire)),
  fetchQuestionaireTypes: () => dispatch(QUESTIONAIRE_TYPE_ACTIONS.fetchQuestionaireTypesAction()),
  fetchCountryCodes: () => dispatch(COUNTRY_CODES_ACTION.fetchCountryCodesAction()),
  changeQuestionnaireStatus: (questionnaire, submit, saveChanges, onFlowProgress) =>
    dispatch(QUESTIONAIRE_ACTIONS.questionnaireStatusAction(questionnaire, submit, saveChanges, onFlowProgress)),
  commitQuestionaire: (questionaire, isNewItem) =>
    dispatch(QUESTIONAIRE_ACTIONS.questionaireSaveAction(questionaire, isNewItem)),
  fetchAllQnaires: () => dispatch(QUESTIONAIRE_ACTIONS.fetchAllQnairesAction()),
  fetchQnaireBRs: (questionaire) => dispatch(QNAIREBR_ACTIONS.fetchbRQnairesAction(questionaire, questionaire.tenantId)),
  fetchTenantCurrencies: (tenant) => dispatch(QUESTIONAIRE_ACTIONS.fetchTenantBalances(tenant)),
  toggleQuestionnaireApiAccess: (questionnaireId, tenantId, enabled) =>
    dispatch(EXTERNAL_API_ACTIONS.toggleQuestionnaireApiAccess(questionnaireId, tenantId, enabled)),
});

export default connect(mapStateToProps, mapDispatchToProps)(QuestionaireDetails);
